import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import * as words from '../../../services/language'
import { MessageService } from '../../../services/message.service';
import { MasterService } from '../../../services/master.service';
import { StoreService } from '../../../services/store.service';
import { formatPhone } from 'src/app/utils/format';
import moment from 'moment';
import { Subject } from "rxjs";

@Component({
    selector: 'app-collect',
    templateUrl: './collect.component.html',
    styleUrls: ['./collect.component.scss'],
    standalone: false
})
export class CollectComponent implements OnInit {

    /** Handle payment taken notifications. */
    protected paymentTakenEvent: Subject<Object> = new Subject();

    /** Notify take payment that we want to collect a certain amount. */
    protected payThisAmountEvent: Subject<number> = new Subject();

    /** Currently selected lot identity. */
    private lotId: number = 0

    /** The collection setup for the lot, dealer or corporation. */
    protected setup: any = null

    /** Save the language */
    protected language: string= 'EN'

    /** Set all words */
    protected words = words.language

    /** Unique internal identity of the contact/customer whose collections will be shown. */
    protected contactId: number = 0

    // The full name of the contact whose payments need to be collected.
    protected contact: string | null = null

    // The primary address of the contact whose payments need to be collected.
    protected address: string | null = null

    // The primary email address of the contact whose payments need to be collected.
    protected email: string | null = null

    // The primary phone/cell number of the contact whose payments need to be collected.
    protected phone: string | null = null

    /** Indicates the component is loading content. */
    protected loading: boolean = false

    /**  */
    protected pays: Object | null = null

    get pastDue() { 
        if (!this.pays || !this.pays['past'] || !this.pays['past']['total']) {
            return 0
        }

        return parseFloat(this.pays['past']['total'])
    }

    get pastDueSince() {
        if (!this.pays || !this.pays['past'] || !this.pays['past']['items'] || (this.pays['past']['items'].length <= 0)) {
            return 'as of today'
        }

        return `since ${moment(this.pays['past']['items'][0].due).format('ddd MMM Do, YYYY')}`
    }

    get dueToday() { 
        if (!this.pays || !this.pays['today'] || !this.pays['today']['total']) {
            return 0
        }

        return parseFloat(this.pays['today']['total'])
    }

    get allocation() {
        if (!this.setup || !this.setup.paymentAllocationOrder) {
            return []
        }

        const items = JSON.parse(this.setup.paymentAllocationOrder)
        if (!items || !Array.isArray(items) || (items.length <= 0)) {
            return []
        }

        const result = []
        for (const item of items) {
            result.push(item.name.toLowerCase())
        }

        return result
    }

    get formattedPhone() {
        if (!this.phone) {
            return null
        }

        return formatPhone(this.phone)
    }

    // **************************
    // ****** life cycles *******
    // **************************

    constructor(private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService, private router: Router, private fb: FormBuilder) { }

    ngOnInit() {
        this.contactId = 0
        const id = parseInt(this.route.snapshot.params['id'])
        if (id && !isNaN(id) && (id > 0)) {
            this.contactId = id
        }

        if (localStorage.getItem('lot')) {
            this.lotId = parseInt(localStorage.getItem('lot'))
        }

        this.readContact()
        this.readSetup()
    }

    // Listen to output of navbar
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'changeLot':
                this.lotId = parseInt(e['lot'])
                this.readSetup()
                break
        }
    }

    private readContact = (): void => {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }
        
        this.master.get(`contacts/${this.contactId}`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.contact) {
                return
            }
            
            this.contact = res.data.contact.name
            this.determineAddress(res.data.contact.contactAddresses)
            this.determineEmail(res.data.contact.contactCommunications)
            this.determinePhone(res.data.contact.contactCommunications)
        })
    }

    private determineAddress = (addresses) => {
        if (!addresses || (addresses.length <= 0)) {
            return
        }

        const primary = addresses.find((addy) => addy.primaryAddress)
        if (!primary) {
            return
        }

        this.address = `${primary.address} ${primary.city}, ${primary.state} ${primary.zipCode}`
    }

    private determineEmail = (communications) => {
        if (!communications || (communications.length <= 0)) {
            return
        }

        const primary = communications.find((comm) => ((comm.type === 'email') && comm.primary))
        if (!primary) {
            return
        }

        this.email = `${primary.value}`
    }

    private determinePhone = (communications) => {
        if (!communications || (communications.length <= 0)) {
            return
        }

        const primary = communications.find((comm) => ((comm.type === 'phone') && comm.primary))
        if (!primary) {
            return
        }

        this.phone = `${primary.value}`
    }

    /**
    * Read the collection setup for the lot, 
    * dealership or corp (what ever is available).
    */
    private readSetup = async () => {
        if (this.setup) {
            return
        }

        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        await this.master.getAsync(`lots/${this.lotId}/collectionSetup`).then(async (res) => {
            if (!res || !res.data || !res.data.collectionSetup) {
                this.setup = null
                return
            }

            this.setup = res.data.collectionSetup
        })
    }

    /**
     * Trigger event to the take payment component with 
     * the current past due amount.
     * @param e Event meta data that triggered this function.
     */
    protected payPastDue = (e: Event) => {
        const toPay = this.pastDue
        if (toPay <= 0) {
            return
        }

        this.payThisAmountEvent.next(toPay)
    }

    /**
     * Trigger event to the take payment component with 
     * the current due today amount.
     * @param e Event meta data that triggered this function.
     */
    protected payDueToday = (e: Event) => {
        const toPay = this.dueToday
        if (toPay <= 0) {
            return
        }

        this.payThisAmountEvent.next(toPay)
    }

    /**
     * Listen for payment changes from the pays component then update 
     * the display and pass the changes to other components.
     * @param pays The payment data showing past, today and next due items.
     */
    protected paymentsChanged = (pays: Object) => {
        this.pays = pays
    }

    /**
     * Listen for payment processing events from the take payment component then update 
     * the display and pass the changes to other components.
     * @param payment The payment details after processing.
     */
    protected paymentTaken = (payment: Object) => {
        this.paymentTakenEvent.next(payment)
    }

    /**
     * Handle refund events from other components.
     * @param id Unique internal identity of the receipt or payment that was refunded.
     */
    protected refunded = (id: number): void => {
        if (!id || (id <= 0)) {
            return
        }

        this.paymentTakenEvent.next(null)
    }

    /**
     * Open the contact editor in a modal.
     * @param e Event that triggered the contact edit.
     */
    protected edit = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        const btn = (document.getElementById('editContactModalBtn_collect') as HTMLButtonElement)
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * Contact was modified in the modal.
     * @param {Object} meta Details about the contact modification.
     */
    protected contactModified = (meta: Object) => {
        this.readContact()
    }
}
